// Local copy of the user's dictionaries (docs/project.md §8): entries of own and shared
// dictionaries plus the lexemes and glosses they reference. Rows have the same shape as the API
// rows, so mergeEntry works over the same types on both sides.
import Dexie, { type EntityTable } from 'dexie';
import type { DictionarySummary, EntryRow, GlossRow, LexemeRow } from '@solungo/core/schema';

export interface SyncStateRow {
  dictionaryId: string;
  cursor: string;
  pulledAt: string;
}

export interface PendingViewRow {
  id?: number;
  dictionaryId: string;
  entryId: string;
  viewedAt: string;
}

// An add request in flight: shown as a spinner row until the server answers (docs/project.md §7).
// The card itself is built by the worker and shows as "building" from the entry's own rows.
export interface PendingAddRow {
  id: string;
  dictionaryId: string;
  headword: string;
  createdAt: string;
  status: 'pending' | 'error';
  errorCode?: string;
}

export class SolungoDb extends Dexie {
  entries!: EntityTable<EntryRow, 'id'>;
  lexemes!: EntityTable<LexemeRow, 'id'>;
  glosses!: EntityTable<GlossRow, 'id'>;
  dictionaries!: EntityTable<DictionarySummary, 'id'>;
  sync_state!: EntityTable<SyncStateRow, 'dictionaryId'>;
  pending_views!: EntityTable<PendingViewRow, 'id'>;
  pending_adds!: EntityTable<PendingAddRow, 'id'>;

  constructor(name = 'solungo') {
    super(name);
    this.version(1).stores({
      entries: 'id, dictionaryId, lexemeId, [dictionaryId+deletedAt], syncSeq',
      lexemes: 'id, [lang+headwordLower]',
      glosses: 'id, lexemeId, [lexemeId+baseLang]',
      dictionaries: 'id, ownerId',
      sync_state: 'dictionaryId',
      pending_views: '++id, dictionaryId, [dictionaryId+entryId]',
      pending_adds: 'id, dictionaryId',
    });
    this.version(2).stores({
      pending_adds: 'id, dictionaryId, createdAt',
    });
  }
}

export const db = new SolungoDb();

// Signing out wipes the database (docs/project.md §8)
export async function wipeDatabase(instance: SolungoDb = db): Promise<void> {
  await instance.transaction(
    'rw',
    [
      instance.entries,
      instance.lexemes,
      instance.glosses,
      instance.dictionaries,
      instance.sync_state,
      instance.pending_views,
      instance.pending_adds,
    ],
    async () => {
      await Promise.all(instance.tables.map((t) => t.clear()));
    },
  );
}
